import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatAmount } from '../../../utils/formatAmount';
import useReport from '../useReport';
import { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import UnpaidDetailsModal from './UnpaidDetailsModal';

const customStylesOrderDetail = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: '80vh',
    width: '80vw',
    marginRight: '0%',
    transform: 'translate(-50%, -50%)',
  },
};

const Row = ({ data, onUnpaidClick }) => {
  const [open, setOpen] = useState(false);
  const [miscOtherOpen, setMiscOtherOpen] = useState(false);

  const handleUnpaidClick = () => {
    onUnpaidClick(data.monthYear, data.month);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{data.month}</TableCell>
        <TableCell align="right">{`${formatAmount(data.revenue.total)} (${data.profits.amountPaidPct}%)`}</TableCell>
        <TableCell align="right">{`${formatAmount(data.fees.totalWeightPrice)} (${data.fees.paidPercentage}%)`}</TableCell>
        <TableCell align="right">{`${formatAmount(data.revenue.total + data.fees.totalWeightPrice)} (${data.profits.totalPaidPct}%)`}</TableCell>
        <TableCell align="right">{formatAmount(data.costs.total)}</TableCell>
        <TableCell align="right" sx={{ color: '#b45309', fontWeight: 600 }}>
          {formatAmount(data.unpaid.amount)}
        </TableCell>
        <TableCell align="right" sx={{ color: '#b45309', fontWeight: 600 }}>
          {formatAmount(data.unpaid.weight)}
        </TableCell>
        <TableCell align="right" sx={{ cursor: 'pointer', color: '#b91c1c', fontWeight: 600, textDecoration: 'underline' }} onClick={handleUnpaidClick}>
          {formatAmount(data.unpaid.total)}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontWeight: 700,
            color: Number(data.profits?.actualProfit ?? 0) >= 0 ? '#166534' : '#991b1b',
          }}
        >
          {formatAmount(data.profits?.actualProfit ?? 0)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

              {/* Amount section */}
              <Paper variant="outlined" sx={{ flex: 2, minWidth: 420, p: 1.5, bgcolor: '#f7fbff' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#0b3a6b' }}>
                  Amount
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#93c5fd' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }} align="right">Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }} align="right">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.revenue.product)} (${Number(data.revenue.product) > 0 ? ((Number(data.paidBreakdown?.product || 0) / Number(data.revenue.product)) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                    <TableCell align="right">{formatAmount(data.costs.amountProduct ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>JP Delivery</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.revenue.delivery)} (${Number(data.revenue.delivery) > 0 ? ((Number(data.paidBreakdown?.delivery || 0) / Number(data.revenue.delivery)) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                    <TableCell align="right">{formatAmount(data.costs.amountDeliveryJp ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Transaction</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.revenue.transaction)} (${Number(data.revenue.transaction) > 0 ? ((Number(data.paidBreakdown?.transaction || 0) / Number(data.revenue.transaction)) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Down Payment</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.revenue.down)} (${(data.revenue.down ?? 0) > 0 ? '100.00' : '0.00'}%)`}
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {`${formatAmount(data.revenue.total)} (${data.profits.amountPaidPct}%)`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>{formatAmount(data.costs.amount)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eaf7ee' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Amount Profit</TableCell>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {`${formatAmount(data.profits.amount)} (${(data.revenue?.total ?? 0) > 0 ? ((data.profits.amount / data.revenue.total) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#0b3a6b' }}>
                  WeightPrice
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#93c5fd' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }} align="right">Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }} align="right">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Weight</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.fees.weightAmount)} (${(data.fees.weightAmount ?? 0) > 0 ? (((data.fees.weightPaid || 0) / data.fees.weightAmount) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                      <TableCell align="right">{formatAmount(data.costs.weight)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Thai Delivery</TableCell>
                      <TableCell align="right">
                        {`${formatAmount(data.fees.thaiDeliveryFee)} (${(data.fees.thaiDeliveryFee ?? 0) > 0 ? (((data.fees.thaiDeliveryPaid || 0) / data.fees.thaiDeliveryFee) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                      <TableCell align="right">{formatAmount(data.costs.thaiDelivery)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {`${formatAmount(data.fees.totalWeightPrice)} (${data.fees.paidPercentage}%)`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>{formatAmount(data.costs.weight + data.costs.thaiDelivery)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eaf7ee' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Weight Profit</TableCell>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {`${formatAmount(data.profits.weight)} (${(data.fees?.totalWeightPrice ?? 0) > 0 ? ((data.profits.weight / data.fees.totalWeightPrice) * 100).toFixed(2) : '0.00'}%)`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Paper variant="outlined" sx={{ mt: 2, p: 1.25, bgcolor: '#eef2ff', borderColor: '#c7d2fe' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#1e1b4b' }}>
                    ส่วนต่างเรทเงิน (แลกเงิน vs เรทรายวัน)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', mb: 1 }}>
                    ต้นทุนในรายงานนี้ใช้ “เรทแลกเงินจริง” แล้ว (กำไร/ขาดทุนนี้สะท้อนใน Amount Profit)
                  </Typography>
                  {(() => {
                    const diff = Number(data.costs.amountCostRateDiff ?? 0); // CurrencyRate - ExchangeMoney (THB)
                    const isProfit = diff > 0; // CurrencyRate higher than ExchangeMoney => profit
                    const displayAmount = Math.abs(diff);
                    const pct = Math.abs(Number(data.costs.amountCostRateDiffPct ?? 0));
                    const tone = isProfit ? '#166534' : '#991b1b';
                    return (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#93c5fd' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#0f172a' }} align="right">
                              Amount (THB)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800, color: tone }}>
                              {isProfit ? 'กำไร' : 'ขาดทุน'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: tone }}>
                              {`${formatAmount(displayAmount)} (${pct.toFixed(2)}%)`}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    );
                  })()}
                </Paper>
              </Paper>

              {/* Costs detail section */}
              <Paper variant="outlined" sx={{ flex: 1, minWidth: 340, p: 1.5, bgcolor: '#fff7ed' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#431407' }}>
                  Costs Detail
                </Typography>
                <Table size="small">
                  <TableBody>
                    {/* Group 1: Amount costs */}
                    <TableRow sx={{ bgcolor: '#fdba74' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#431407' }}>Amount Costs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                        {formatAmount(data.costs.amount)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>Product Cost</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.amountProduct ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>JP Delivery Cost</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.amountDeliveryJp ?? 0)}</TableCell>
                    </TableRow>

                    {/* Group 2: Weight & delivery costs */}
                    <TableRow sx={{ bgcolor: '#fdba74' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#431407' }}>Weight & Delivery Costs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                        {formatAmount((data.costs.weight ?? 0) + (data.costs.thaiDelivery ?? 0))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>ค่าน้ำหนักจ่ายคลัง</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.weight)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>Thai Delivery Cost</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.thaiDelivery)}</TableCell>
                    </TableRow>

                    {/* Group 3: Admin costs (Expense: เงินเดือน admin / own salary / bonus admin) */}
                    <TableRow sx={{ bgcolor: '#fdba74' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#431407' }}>Admin Costs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                        {formatAmount(
                          (data.costs.adminSalary ?? 0) +
                            (data.costs.adminBonus ?? 0) +
                            (data.costs.ownSalary ?? 0)
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>เงินเดือน Admin</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.adminSalary)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>Bonus Admin</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.adminBonus)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>Own Salary</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.ownSalary ?? 0)}</TableCell>
                    </TableRow>

                    {/* Group 4: ค่าฝากซื้อ (above Other Cost) */}
                    {(data.costs.orderByOptions?.length > 0 || (data.costs.orderByOptionsTotal ?? 0) !== 0) && (
                      <>
                        <TableRow sx={{ bgcolor: '#fdba74' }}>
                          <TableCell sx={{ fontWeight: 700, color: '#431407' }}>ค่าฝากซื้อ</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                            {formatAmount(data.costs.orderByOptionsTotal ?? 0)}
                          </TableCell>
                        </TableRow>
                        {(data.costs.orderByOptions ?? []).map((opt) => (
                          <TableRow key={`obo-${opt.optionId}`}>
                            <TableCell sx={{ pl: 2, color: '#1c1917' }}>{opt.label}</TableCell>
                            <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(opt.cost)}</TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {/* Group 5: Refund (customer refunds by reason) */}
                    <TableRow sx={{ bgcolor: '#fdba74' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#431407' }}>Refund</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                        {formatAmount(data.costs.refundTotal ?? 0)}
                      </TableCell>
                    </TableRow>
                    {(data.costs.refundByReason ?? []).map((r) => (
                      <TableRow key={`refund-${r.reason}`}>
                        <TableCell sx={{ pl: 2, color: '#1c1917' }}>{r.label}</TableCell>
                        <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(r.amount ?? 0)}</TableCell>
                      </TableRow>
                    ))}

                    {/* Group 6: Other Cost — withdraw, ค่าเช่าไอดี mercari, อื่นๆ */}
                    <TableRow sx={{ bgcolor: '#fdba74' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#431407' }}>Other Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#431407' }}>
                        {formatAmount(
                          (data.costs.otherCostTotal ?? 0) - (data.costs.refundTotal ?? 0)
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>ค่าธรรมเนียมถอนเงิน</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.withdrawFee ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>VAT</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.vatTotal ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>ค่าเช่าไอดี mercari</TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.mercariRentTotal ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 2, color: '#1c1917' }}>
                        <IconButton
                          size="small"
                          aria-label={miscOtherOpen ? 'hide detail' : 'show detail'}
                          onClick={() => setMiscOtherOpen((v) => !v)}
                          sx={{ mr: 0.5, p: 0.25 }}
                        >
                          {miscOtherOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        อื่นๆ
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#1c1917' }}>{formatAmount(data.costs.miscOtherTotal ?? 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ py: 0, borderBottom: 'none', px: 0 }}>
                        <Collapse in={miscOtherOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ pl: 3, pr: 1, pb: 1, pt: 0.5, bgcolor: '#fffbeb' }}>
                            <Table size="small">
                              <TableBody>
                                {(data.costs.miscOtherLines ?? []).length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={2} sx={{ color: '#78716c', fontSize: '0.85rem', border: 0 }}>
                                      No lines for this month
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  (data.costs.miscOtherLines ?? []).map((line) => (
                                    <TableRow key={`misc-${line.expenseId}`}>
                                      <TableCell sx={{ color: '#1c1917', border: 0, maxWidth: 220 }}>{line.note}</TableCell>
                                      <TableCell align="right" sx={{ color: '#1c1917', border: 0 }}>{formatAmount(line.cost)}</TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ bgcolor: '#e7e5e4' }}>
                      <TableCell sx={{ color: '#0c0a09', fontWeight: 700 }}>Total Cost</TableCell>
                      <TableCell align="right" sx={{ color: '#0c0a09', fontWeight: 700 }}>{formatAmount(data.costs.total)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const MonthlyProfitReport = () => {
  const { getMonthlyProfitReport } = useReport();
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [formattedMonth, setFormattedMonth] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getMonthlyProfitReport();
      setAllData(response || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const shopOptions = useMemo(() => {
    const shops = new Set(allData.filter(r => r.shop !== 'all').map(r => r.shop));
    return ['all', ...Array.from(shops).sort()];
  }, [allData]);

  const filteredData = useMemo(() => {
    return allData.filter(r => r.shop === selectedShop);
  }, [allData, selectedShop]);

  const handleUnpaidClick = (formattedMonth, displayMonth) => {
    setSelectedMonth(displayMonth);
    setFormattedMonth(formattedMonth);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMonth(null);
    setFormattedMonth(null);
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        style={customStylesOrderDetail}
        ariaHideApp={false}
      >
        <UnpaidDetailsModal
          key={selectedMonth && formattedMonth ? `${formattedMonth}-${selectedMonth}` : 'unpaid-modal-closed'}
          month={selectedMonth}
          formattedMonth={formattedMonth}
        />
      </Modal>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={selectedShop}
          exclusive
          onChange={(e, val) => { if (val !== null) setSelectedShop(val); }}
          size="small"
        >
          {shopOptions.map(shop => (
            <ToggleButton key={shop} value={shop}>
              {shop === 'all' ? 'All Shops' : shop}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table" size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1565c0' }}>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} />
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Month</TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">
                <Tooltip title="Amount section total: Product + JP Delivery + Transaction + Down Payment" arrow>
                  <span>TotalProduct (Paid%)</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">
                <Tooltip title="Weight section total: Weight + Thai Delivery Fee" arrow>
                  <span>TotalWeight (Paid%)</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">
                <Tooltip title="Total = TotalProduct + TotalWeight (paid% uses paid/total for all)" arrow>
                  <span>Total</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">Total Cost</TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">Amount Unpaid</TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">Weight Unpaid</TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">Total Unpaid</TableCell>
              <TableCell sx={{ color: '#ffffff', fontWeight: 700 }} align="right">
                <Tooltip
                  title="Actual Profit = Total Paid − Total Cost. Total Paid is amount-section paid (product/JP/tx/down) plus weight paid plus Thai delivery paid."
                  arrow
                >
                  <span>Actual Profit</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, index) => (
              <Row 
                key={`${row.month}-${row.shop}-${index}`} 
                data={row} 
                onUnpaidClick={handleUnpaidClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MonthlyProfitReport;